'use client';
import React, { useState } from 'react';
import WalkthroughPassagePanel from './WalkthroughPassagePanel';
import WalkthroughQuestionPanel from './WalkthroughQuestionPanel';
import WalkthroughSidebar from './WalkthroughSidebar';
import { BookOpen, MessageSquare, Lightbulb, ChevronLeft, ChevronRight } from 'lucide-react';

const WALKTHROUGH_QUESTIONS = [
  {
    id: 'wt-q1',
    typeCode: 'TF/NG',
    typeLabel: 'True / False / Not Given',
    difficulty: 'Medium',
    difficultyIndex: 61,
    keywordEffectiveness: 'Low',
    mainIdeaPattern: 'Contrast structure — key fact delivered as qualification ("though…")',
    question: 'The number of wild pollinators in cities has increased at the same rate as urban beehives.',
    correctAnswer: 'False',
    correctReasoning: `The passage states that "the increase in hive numbers has not been matched by an equivalent rise in wild pollinator populations." The question rewrites this as "increased at the same rate" — which the passage directly contradicts. The qualifying clause "not been matched by" is the critical phrase that makes this False, not Not Given.`,
    paraphraseMap: [
      { id: 'pm-1', passageText: 'increase in hive numbers', questionText: 'urban beehives' },
      { id: 'pm-2', passageText: 'not been matched by an equivalent rise', questionText: 'increased at the same rate (negated)' },
      { id: 'pm-3', passageText: 'wild pollinator populations', questionText: 'wild pollinators in cities' },
    ],
    synonymMap: [
      { id: 'syn-1', passageWord: 'equivalent rise', questionWord: 'same rate' },
      { id: 'syn-2', passageWord: 'hive numbers', questionWord: 'urban beehives' },
    ],
    distractors: [
      {
        id: 'dist-1',
        answer: 'True',
        trapType: 'Partial keyword match + unread negation',
        explanation: `A candidate who matches "urban," "beekeeping," and "increase" without reading the qualifying clause "has not been matched by" may assume the statement is confirmed. The trap is scanning for keywords and stopping before the negation — a surface match followed by an unread reversal.`,
        howToAvoid: 'Always read the complete sentence before deciding. Negation words ("not," "without," "no") typically appear in the second half of a clause — finish the sentence before marking True.',
      },
      {
        id: 'dist-2',
        answer: 'Not Given',
        trapType: 'Over-caution — passage does address this',
        explanation: `Some candidates mark Not Given because the passage does not give exact numbers. But Not Given means the passage is completely silent on the claim — here, the passage directly contradicts it. A contradiction is False, not Not Given, even when no figures are provided.`,
        howToAvoid: 'Ask: "Does the passage say the opposite?" If yes → False. Only use Not Given when the passage neither confirms nor contradicts.',
      },
    ],
    strategyTip: {
      title: 'True / False / Not Given: The Three-Step Decision',
      steps: [
        { id: 'step-1', text: 'Find the exact location in the passage where the statement is addressed.' },
        { id: 'step-2', text: 'Read the complete sentence — do not stop at the first keyword match.' },
        { id: 'step-3', text: 'Ask: does the passage confirm it (True), contradict it (False), or not address it at all (Not Given)?' },
      ],
    },
    highlightedParagraph: 'B',
    highlightedText: 'the increase in hive numbers has not been matched by an equivalent rise in wild pollinator populations',
  },
  {
    id: 'wt-q2',
    typeCode: 'TF/NG',
    typeLabel: 'True / False / Not Given',
    difficulty: 'Hard',
    difficultyIndex: 44,
    keywordEffectiveness: 'Very Low',
    mainIdeaPattern: 'Absence of information — passage is completely silent on this claim',
    question: 'Ecologists universally support the expansion of urban beekeeping programmes.',
    correctAnswer: 'Not Given',
    correctReasoning: `The passage says "some ecologists argue" that wild pollinators need more flowering species — it does not state whether ecologists support or oppose urban beekeeping expansion. The word "universally" would require the passage to make a claim about all ecologists, which it never does. The passage is silent on ecologist opinion about expansion itself.`,
    paraphraseMap: [
      { id: 'pm-ng-1', passageText: 'some ecologists argue (about flowering species)', questionText: 'ecologists universally support (expansion)' },
    ],
    synonymMap: [
      { id: 'syn-ng-1', passageWord: 'some ecologists', questionWord: 'ecologists universally' },
    ],
    distractors: [
      {
        id: 'dist-ng-1',
        answer: 'True',
        trapType: 'False inference from positive framing',
        explanation: `The passage describes urban beekeeping growth positively in Paragraph A, which can lead candidates to infer that ecologists support it. But the passage never states this — inference is not evidence. True requires a direct statement, not a reasonable assumption.`,
        howToAvoid: 'Separate what the passage says from what you can reasonably infer. IELTS rewards what is stated, not what is implied.',
      },
      {
        id: 'dist-ng-2',
        answer: 'False',
        trapType: 'Over-reading "some ecologists argue" as opposition',
        explanation: `"Some ecologists argue" about flowering species diversity — this is a concern, not a statement of opposition to beekeeping expansion. Candidates who read this as ecologists being against expansion mark False, but the passage never says ecologists oppose it.`,
        howToAvoid: 'A concern or caveat is not the same as opposition. Only mark False if the passage directly contradicts the statement.',
      },
    ],
    strategyTip: {
      title: 'The False vs. Not Given Distinction',
      steps: [
        { id: 'step-ng-1', text: 'False = the passage says the OPPOSITE of the statement.' },
        { id: 'step-ng-2', text: 'Not Given = the passage does not address the statement at all.' },
        { id: 'step-ng-3', text: 'Test: can you find a sentence in the passage that directly contradicts the statement? If no → Not Given.' },
      ],
    },
    highlightedParagraph: 'A',
    highlightedText: 'some ecologists argue depend on a broader range of flowering species than cities typically provide',
  },
  {
    id: 'wt-q3',
    typeCode: 'MATCH_HEAD',
    typeLabel: 'Matching Headings',
    difficulty: 'Hard',
    difficultyIndex: 48,
    keywordEffectiveness: 'Low',
    mainIdeaPattern: 'Main idea stated across multiple sentences — no single topic sentence',
    question: 'Paragraph B: Choose the correct heading from the list.',
    correctAnswer: 'v  The role of flowering plants in pollinator health',
    correctReasoning: `Paragraph B discusses how wild pollinators need a broader range of flowering species than cities provide, and how managed honeybee competition may disadvantage wild species with narrower habitat requirements. The central concern is the relationship between flowering plant diversity and wild pollinator health. This is Heading v.`,
    paraphraseMap: [
      { id: 'pm-mh-1', passageText: 'broader range of flowering species', questionText: 'role of flowering plants' },
      { id: 'pm-mh-2', passageText: 'wild pollinator populations', questionText: 'pollinator health' },
    ],
    synonymMap: [
      { id: 'syn-mh-1', passageWord: 'flowering species', questionWord: 'flowering plants' },
      { id: 'syn-mh-2', passageWord: 'wild pollinators disadvantaged', questionWord: 'pollinator health (threatened)' },
    ],
    distractors: [
      {
        id: 'dist-mh-1',
        answer: 'iii  A mismatch between beekeeping expansion and pollinator recovery',
        trapType: 'True of a different paragraph — Paragraph A, not B',
        explanation: `Heading iii matches the overall passage theme and contains keywords ("beekeeping," "pollinator") that appear in Paragraph B. But Paragraph A establishes the mismatch between hive growth and wild pollinator growth. Paragraph B focuses on why — the flowering species requirement. Picking iii for Paragraph B is a keyword match without reading the paragraph's main idea.`,howToAvoid: 'For Matching Headings, skim each paragraph for its main idea, not just keywords. The correct heading summarises the whole paragraph — not just one sentence in it.',
      },
      {
        id: 'dist-mh-2',answer: 'ii  Urban growth and its effect on native species',trapType: 'Too broad — paragraph is more specific',explanation: `Heading ii is plausible because Paragraph B is about urban environments and native species. But it is too broad — "urban growth" is not the paragraph's focus. The paragraph is specifically about the relationship between flowering plant diversity and wild pollinator viability, which Heading v captures precisely.`,
        howToAvoid: 'When two headings both seem plausible, ask which one is more specific to the paragraph\'s actual content. The correct heading is the more precise match.',
      },
    ],
    strategyTip: {
      title: 'Matching Headings: Skim for Main Idea, Not Keywords',
      steps: [
        { id: 'step-mh-1', text: 'Read the heading list first — identify the 2–3 that seem most plausible for each paragraph.' },
        { id: 'step-mh-2', text: 'Skim the paragraph for its topic sentence and concluding sentence — these carry the main idea.' },
        { id: 'step-mh-3', text: 'Eliminate headings that are true of a different paragraph or too broad/narrow for this one.' },
      ],
    },
    highlightedParagraph: 'B',
    highlightedText: 'broader range of flowering species than cities typically provide',
  },
];

type MobileTab = 'passage' | 'walkthrough' | 'insights';

export default function AnnotatedWalkthroughScreen() {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [understood, setUnderstood] = useState<Set<string>>(new Set());
  const [confused, setConfused] = useState<Set<string>>(new Set());
  const [mobileTab, setMobileTab] = useState<MobileTab>('walkthrough');

  const currentQ = WALKTHROUGH_QUESTIONS?.[currentIdx];
  const total = WALKTHROUGH_QUESTIONS?.length;

  const markUnderstood = () => {
    setUnderstood((p) => { const n = new Set(p); n?.add(currentQ?.id); return n; });
    if (currentIdx < total - 1) setCurrentIdx((p) => p + 1);
  };

  const markConfused = () => {
    setConfused((p) => { const n = new Set(p); n?.add(currentQ?.id); return n; });
  };

  const goToPrev = () => setCurrentIdx((p) => Math.max(0, p - 1));
  const goToNext = () => setCurrentIdx((p) => Math.min(total - 1, p + 1));

  const MOBILE_TABS: { key: MobileTab; label: string; icon: React.ReactNode }[] = [
    { key: 'passage', label: 'Passage', icon: <BookOpen size={15} /> },
    { key: 'walkthrough', label: 'Walkthrough', icon: <MessageSquare size={15} /> },
    { key: 'insights', label: 'Insights', icon: <Lightbulb size={15} /> },
  ];

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background">
      {/* ── Top bar ── */}
      <div className="flex-shrink-0 bg-card border-b border-border px-3 sm:px-5 py-2.5 sm:py-3 flex items-center justify-between gap-2 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-sm sm:text-base font-semibold text-foreground truncate">Annotated Walkthrough</h1>
          <p className="text-xs text-muted-foreground truncate hidden sm:block">True / False / Not Given · Passage 1 — Urban Beekeeping</p>
        </div>

        {/* Question navigator dots + counter */}
        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
          <div className="flex items-center gap-1">
            {WALKTHROUGH_QUESTIONS?.map((q, i) => (
              <button
                key={`wt-dot-${q?.id}`}
                onClick={() => setCurrentIdx(i)}
                aria-label={`Question ${i + 1}`}
                className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full text-xs font-bold border-2 transition-all duration-150 ${
                  i === currentIdx
                    ? 'border-primary bg-primary text-primary-foreground'
                    : understood?.has(q?.id)
                    ? 'border-accent bg-success-bg text-accent'
                    : confused?.has(q?.id)
                    ? 'border-warning bg-warning-bg text-warning' :'border-border bg-card text-muted-foreground hover:border-primary/40'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-md tabular-nums hidden sm:inline">
            {currentIdx + 1} / {total}
          </span>
          {/* Mobile prev/next arrows in top bar */}
          <div className="flex items-center gap-1 sm:hidden">
            <button
              onClick={goToPrev}
              disabled={currentIdx === 0}
              className="w-7 h-7 flex items-center justify-center rounded-lg border border-border bg-card text-muted-foreground disabled:opacity-30 hover:bg-muted transition-colors"
              aria-label="Previous question"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={goToNext}
              disabled={currentIdx === total - 1}
              className="w-7 h-7 flex items-center justify-center rounded-lg border border-border bg-card text-muted-foreground disabled:opacity-30 hover:bg-muted transition-colors"
              aria-label="Next question"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile tab bar (hidden on lg+) ── */}
      <div className="flex-shrink-0 lg:hidden bg-card border-b border-border">
        <div className="flex">
          {MOBILE_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setMobileTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold transition-colors border-b-2 ${
                mobileTab === tab.key
                  ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Main content area ── */}
      <div className="flex-1 min-h-0 flex overflow-hidden">

        {/* PASSAGE — always visible on lg+, tab-controlled on mobile */}
        <div className={`
          lg:w-[42%] lg:flex-shrink-0 lg:border-r lg:border-border lg:flex lg:flex-col lg:min-h-0
          ${mobileTab === 'passage' ? 'flex flex-col flex-1 min-h-0' : 'hidden lg:flex'}
        `}>
          <WalkthroughPassagePanel
            highlightedParagraph={currentQ?.highlightedParagraph}
            highlightedText={currentQ?.highlightedText}
          />
        </div>

        {/* WALKTHROUGH QUESTION PANEL — always visible on lg+, tab-controlled on mobile */}
        <div className={`
          lg:flex lg:flex-col lg:flex-1 lg:min-h-0 lg:overflow-hidden
          ${mobileTab === 'walkthrough' ? 'flex flex-col flex-1 min-h-0 overflow-hidden' : 'hidden lg:flex'}
        `}>
          <WalkthroughQuestionPanel
            question={currentQ}
            understood={understood?.has(currentQ?.id)}
            confused={confused?.has(currentQ?.id)}
            onUnderstood={markUnderstood}
            onConfused={markConfused}
            onPrev={goToPrev}
            onNext={goToNext}
            isFirst={currentIdx === 0}
            isLast={currentIdx === total - 1}
          />
        </div>

        {/* INSIGHTS SIDEBAR — always visible on lg+, tab-controlled on mobile */}
        <div className={`
          lg:flex lg:flex-col lg:min-h-0
          ${mobileTab === 'insights' ? 'flex flex-col flex-1 min-h-0 overflow-hidden' : 'hidden lg:flex'}
        `}>
          <WalkthroughSidebar question={currentQ} />
        </div>
      </div>
    </div>
  );
}